import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { formatIsoDateToItalian as formatDate } from '../utils/utils';

/* Palette */
const C = {
    black:        '#1a1a1a',
    darkGray:     '#333333',
    midGray:      '#666666',
    lightGray:    '#f0f0f0',
    silver:       '#cccccc',
    accent:       '#8b0000',
    accentLight:  '#f9eeee',
    headerBg:     '#1c2b4a',
    sectionBg:    '#dde3ec',
    sectionBorder:'#1c2b4a',
    fieldLine:    '#888888',
    white:        '#ffffff',
    sigBorder:    '#d32f2f', // bordo rosso dei riquadri firma cliente
};

/* Stili */
const styles = StyleSheet.create({

    /* Pagina, allineata a InformativaPrivacyPDF. Il padding inferiore lascia spazio
       al numero pagina fisso sotto il footer. */
    page: {
        paddingBottom: 18,
        paddingHorizontal: 35,
        fontSize: 9,
        fontFamily: 'Times-Roman',
        lineHeight: 1.45,
        color: C.black,
    },

    /* Header fisso, come in InformativaPrivacyPDF. */
    header: {
        paddingTop: 0,
        paddingBottom: 4,
    },
    headerBanner: {
        width: '100%',
        // Altezza limitata come negli altri PDF: un header alto altrimenti occupava
        // mezza pagina 1 e spingeva il contenuto su una pagina bianca.
        height: 75,
        objectFit: 'contain',
    },
    headerInner: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingTop: 10,
        paddingBottom: 6,
    },
    logo: {
        width: 50,
        height: 40,
        objectFit: 'contain',
    },
    headerTextBlock: {
        flex: 1,
        paddingLeft: 10,
    },
    headerAgency: {
        fontSize: 12,
        fontFamily: 'Times-Roman',
        color: C.black,
        letterSpacing: 0.3,
    },
    headerSub: {
        fontSize: 7.5,
        color: C.midGray,
        marginTop: 1,
    },
    headerAccentBar: {
        height: 2,
        backgroundColor: C.accent,
    },
    headerMeta: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingTop: 3,
        paddingBottom: 3,
        borderTopWidth: 0.5,
        borderTopColor: C.silver,
    },
    headerConfidential: {
        fontSize: 7,
        color: C.accent,
        fontStyle: 'italic',
        letterSpacing: 0.5,
    },
    headerPageNum: {
        fontSize: 7,
        color: C.midGray,
        textAlign: 'right',
    },

    /* Titolo documento (prima pagina di ogni soggetto) */
    docTitleBar: {
        marginTop: 8,
        marginBottom: 8,
    },
    docTitleMain: {
        fontSize: 11,
        fontFamily: 'Times-Roman',
        color: C.headerBg,
        letterSpacing: 0.3,
        marginBottom: 2,
        textTransform: 'uppercase',
    },

    /* Barra data / rif */
    dateRefRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        marginBottom: 10,
        paddingBottom: 6,
        borderBottomWidth: 0.75,
        borderBottomColor: C.silver,
    },
    dateRefItem: {
        flexDirection: 'row',
        alignItems: 'flex-end',
    },
    dateRefLabel: {
        fontSize: 9,
        fontFamily: 'Times-Roman',
        color: C.darkGray,
        marginRight: 6,
    },
    dateRefValue: {
        fontSize: 9.5,
        borderBottomWidth: 0.75,
        borderBottomColor: C.fieldLine,
        minWidth: 110,
        paddingBottom: 1,
    },

    /* Footer non fixed, spinto in fondo con marginTop: 'auto' come in BonificaPDF/ConsulenzaPDF.
       Con position fixed OTP trovava l'ancora #otp_sf{n}# in alto invece che nella casella. */
    footer: {
        marginTop: 'auto',
        borderTopWidth: 0.5,
        borderTopColor: C.silver,
        paddingVertical: 6,
        flexDirection: 'row',
        alignItems: 'flex-end',
        justifyContent: 'space-between',
    },
    footerCopyright: {
        fontSize: 6.5,
        color: C.midGray,
        fontStyle: 'italic',
        textAlign: 'left',
        flex: 1,
    },
    footerSignature: {
        flexDirection: 'column',
        alignItems: 'flex-start',
        marginLeft: 16,
        minWidth: 130,
    },
    footerSignatureLabel: {
        fontSize: 7,
        color: C.midGray,
        marginBottom: 3,
    },
    footerSignatureLine: {
        width: 130,
        height: 26,
        borderWidth: 0.75,
        borderColor: C.sigBorder,
        backgroundColor: '#fafafa',
    },
    // Numero pagina fisso, centrato in basso e separato dalla casella firma
    // (in basso a destra) per non confonderlo con l'ancora OTP.
    footerPageNum: {
        position: 'absolute',
        bottom: 8,
        left: 0,
        right: 0,
        textAlign: 'center',
        fontSize: 8,
        color: '#888888',
    },

    /* Titolo sezione */
    sectionTitle: {
        backgroundColor: C.sectionBg,
        borderLeftWidth: 3,
        borderLeftColor: C.sectionBorder,
        paddingVertical: 4,
        paddingHorizontal: 8,
        marginTop: 12,
        marginBottom: 6,
        fontSize: 9,
        fontFamily: 'Times-Roman',
        color: C.headerBg,
        letterSpacing: 0.3,
        textTransform: 'uppercase',
    },

    /* Campi */
    fieldRow: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        marginBottom: 5,
    },
    fieldLabel: {
        fontFamily: 'Times-Roman',
        fontSize: 9,
        color: C.darkGray,
        marginRight: 5,
    },
    fieldValue: {
        flex: 1,
        borderBottomWidth: 0.75,
        borderBottomColor: C.fieldLine,
        fontSize: 9.5,
        paddingBottom: 2,
        minHeight: 14,
        color: C.black,
    },

    /* Casella di testo libero: interlinea stretta per non sprecare spazio sulle domande aperte. */
    textBox: {
        borderWidth: 0.75,
        borderColor: C.silver,
        backgroundColor: '#fafafa',
        marginBottom: 8,
        padding: 5,
    },
    textBoxLine: {
        borderBottomWidth: 0.3,
        borderBottomColor: '#ddd',
        minHeight: 10,
        marginBottom: 1,
    },
    textBoxValue: {
        fontSize: 9.5,
        color: C.black,
        lineHeight: 1.15,
    },

    /* Righe Sì/No compatte, per far stare le valutazioni di sicurezza in una pagina. */
    boolRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 2,
        paddingVertical: 1,
        borderBottomWidth: 0.3,
        borderBottomColor: '#e0e0e0',
    },
    boolLabel: {
        flex: 1,
        fontSize: 9.5,
        fontFamily: 'Times-Roman',
        color: C.black,
        paddingRight: 10,
    },
    boolOptions: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    boolBox: {
        width: 12,
        height: 12,
        borderWidth: 1,
        borderColor: C.darkGray,
        borderRadius: 1,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 4,
        backgroundColor: C.white,
    },
    boolTick: {
        fontSize: 8,
        color: C.accent,
        lineHeight: 1,
    },
    boolText: {
        fontSize: 9,
        fontFamily: 'Times-Roman',
        color: C.darkGray,
        marginRight: 14,
    },

    /* Nota / avviso */
    noteBox: {
        borderLeftWidth: 2,
        borderLeftColor: C.accent,
        backgroundColor: '#fffbf0',
        paddingLeft: 8,
        paddingVertical: 5,
        paddingRight: 5,
        marginVertical: 8,
    },
    noteText: {
        fontSize: 9,
        color: C.darkGray,
        fontStyle: 'italic',
        lineHeight: 1.5,
        textAlign: 'justify',
    },

    /* Firma finale */
    signatureRow: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        marginTop: 18,
        gap: 20,
    },
    signatureLabel: {
        fontSize: 9.5,
        fontFamily: 'Times-Roman',
        color: C.darkGray,
        marginRight: 8,
    },
    signatureBox: {
        borderWidth: 0.75,
        borderColor: C.sigBorder,
        backgroundColor: '#fafafa',
        // Larga come le firme nel testo del mandato (60%), non fino al margine.
        width: '60%',
        height: 35,
        flexDirection: 'row',
        alignItems: 'center',
        paddingLeft: 6,
    },
    signatureArrow: {
        fontSize: 13,
        color: C.accent,
        marginRight: 4,
    },
});

/* Helper */

const Field = ({ label, value, style }: { label: string; value?: any; style?: any }) => (
    <View style={[styles.fieldRow, style]}>
        <Text style={styles.fieldLabel}>{label}</Text>
        <Text style={styles.fieldValue}>{value || ''}</Text>
    </View>
);

const TextBox = ({ value, lines = 4 }: { value?: any; lines?: number }) => (
    // Altezza minima in linea con le righe strette (~11pt per riga + 10 di padding).
    <View style={[styles.textBox, { minHeight: lines * 11 + 10 }]}>
        {value
            ? <Text style={styles.textBoxValue}>{value}</Text>
            : Array.from({ length: lines }).map((_, i) => (
                <View key={i} style={styles.textBoxLine} />
            ))
        }
    </View>
);

// inBianco: modulo da compilare a penna, né SÌ né NO spuntati.
const BoolField = ({ label, value, inBianco }: { label: string; value?: any; inBianco?: boolean }) => {
    const checked = value === true || value === 'true' || value === 1;
    return (
        <View style={styles.boolRow}>
            <Text style={styles.boolLabel}>{label}</Text>
            <View style={styles.boolOptions}>
                <View style={styles.boolBox}>
                    {!checked && !inBianco && <View style={{ width: 7, height: 7, backgroundColor: C.accent }} />}
                </View>
                <Text style={styles.boolText}>NO</Text>
                <View style={styles.boolBox}>
                    {checked && <View style={{ width: 7, height: 7, backgroundColor: C.accent }} />}
                </View>
                <Text style={styles.boolText}>SÌ</Text>
            </View>
        </View>
    );
};

const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <Text style={styles.sectionTitle}>{children}</Text>
);

/* Header */
const PageHeader = ({ logoUrl, dataCompilazione, rifero, settings, index, total, pageNum, totalPages }: {
    logoUrl?: any; dataCompilazione?: any; rifero?: any; settings?: any;
    index: number; total: number; pageNum: number; totalPages: number;
}) => {
    const bannerUrl = settings?.mandatoHeaderUrl || settings?.mandatoHeaderImageUrl || settings?.reportHeaderUrl;
    const agencyName = settings?.agencyName || '';
    const suffixStr = total > 1 ? ` – SOGGETTO N. ${index + 1}` : '';

    return (
        <View style={styles.header} fixed>
            {bannerUrl ? (
                <Image src={bannerUrl} style={styles.headerBanner} />
            ) : (
                <View style={styles.headerInner}>
                    {logoUrl && <Image src={logoUrl} style={styles.logo} />}
                    <View style={styles.headerTextBlock}>
                        <Text style={styles.headerAgency}>{agencyName}</Text>
                        <Text style={styles.headerSub}>Investigazioni Private · Sicurezza · Consulenza</Text>
                    </View>
                </View>
            )}
            <View style={styles.headerAccentBar} />
            <View style={styles.headerMeta}>
                <Text style={styles.headerConfidential}>
                    Prospetto Dati Soggetto Investigato{suffixStr} · Documento riservato
                </Text>
                <Text style={styles.headerPageNum}>Pag. {pageNum} di {totalPages}</Text>
            </View>
        </View>
    );
};

/* Footer non fixed con pageNum esplicito, in coda a ogni pagina e spinto in fondo
   con marginTop: 'auto'. */
const PageFooter = ({ pageNum }: { pageNum: number }) => (
    <View style={styles.footer}>
        <Text style={styles.footerCopyright}>
            Il presente documento costituisce opera dell'ingegno ai sensi della normativa vigente ed è riservato all'uso esclusivo dei soggetti espressamente autorizzati dall'autore. È vietata qualsiasi forma di riproduzione, comunicazione, diffusione o utilizzazione, anche parziale, in assenza di preventiva autorizzazione scritta.
        </Text>
        <View style={styles.footerSignature}>
            <Text style={styles.footerSignatureLabel}>Firma Mandante:</Text>
            <View style={styles.footerSignatureLine}>
                <Text style={{ fontSize: 0.01, color: '#ffffff' }}>{`#otp_sf${pageNum}#`}</Text>
            </View>
        </View>
    </View>
);

/* Documento */

export const DatiSoggettoPDF = ({ data }: { data: any }) => {
    const { pratica, logoUrl, settings } = data;

    // Accetta sia un array sia un singolo oggetto
    const soggetti = pratica?.soggetti && pratica.soggetti.length > 0
        ? pratica.soggetti
        : [{
            nome:              pratica?.soggetto_nome || pratica?.soggetto_denominazione,
            nato_a:            pratica?.soggetto_nato_a,
            data_nascita:      pratica?.soggetto_data_nascita,
            indirizzo:         pratica?.soggetto_indirizzo,
            cap:               pratica?.soggetto_cap,
            citta:             pratica?.soggetto_citta,
            prov:              pratica?.soggetto_prov,
            codice_fiscale:    pratica?.soggetto_codice_fiscale,
            sede_via:          pratica?.soggetto_sede_via,
            sede_cap:          pratica?.soggetto_sede_cap,
            sede_citta:        pratica?.soggetto_sede_citta,
            sede_prov:         pratica?.soggetto_sede_prov,
            piva:              pratica?.soggetto_piva,
            telefono:          pratica?.soggetto_telefono,
            email:             pratica?.soggetto_email,
            social:            pratica?.soggetto_social,
            domicilio_altrove: pratica?.soggetto_domicilio_altrove,
            luoghi_frequentati:pratica?.soggetto_luoghi_frequentati,
            altre_info_casa:   pratica?.soggetto_altre_info_casa,
            auto:              pratica?.soggetto_auto || pratica?.soggetto_mezzi,
            auto_conoscenti:   pratica?.soggetto_auto_conoscenti,
            moto:              pratica?.soggetto_moto,
            lavoro:            pratica?.soggetto_lavoro,
            hobby:             pratica?.soggetto_hobby,
            abitudini:         pratica?.soggetto_abitudini,
            fisico:            pratica?.soggetto_fisico,
            gia_pedinato:      pratica?.soggetto_gia_pedinato,
            attento:           pratica?.soggetto_attento,
            lavoro_particolare:pratica?.soggetto_lavoro_particolare,
            pericoloso:        pratica?.soggetto_pericoloso,
            armi:              pratica?.soggetto_armi,
            allegati_nomi:     pratica?.soggetto_allegati_nomi,
        }];

    const totalPages = soggetti.length * 3; // 3 pagine per soggetto

    return (
        <Document>
            {soggetti.map((soggetto: any, index: number) => {
                const base = index * 3; // numero pagina base per questo soggetto
                return (
                    <React.Fragment key={index}>

                        {/* Pagina 1: anagrafica */}
                        <Page size="A4" style={styles.page}>
                            <PageHeader logoUrl={logoUrl} dataCompilazione={pratica?.data_compilazione}
                                        rifero={pratica?.rif_numero} settings={settings}
                                        index={index} total={soggetti.length} pageNum={base + 1} totalPages={totalPages} />
                            <Text fixed style={styles.footerPageNum}
                                  render={({ pageNumber, totalPages }) => `Pag. ${pageNumber} di ${totalPages}`} />

                            {/* Titolo su una riga sola, "Documento riservato" è già nell'header. */}
                            <View style={styles.docTitleBar}>
                                <Text style={styles.docTitleMain}>
                                    Prospetto Dati Soggetto Investigato
                                    {soggetti.length > 1 ? `  –  Soggetto n. ${index + 1}` : ''}
                                </Text>
                            </View>

                            {/* Data compilazione / Rif */}
                            <View style={styles.dateRefRow}>
                                <View style={styles.dateRefItem}>
                                    <Text style={styles.dateRefLabel}>Data compilazione:</Text>
                                    <Text style={styles.dateRefValue}>{formatDate(pratica?.data_incarico || pratica?.data_compilazione || '')}</Text>
                                </View>
                                <View style={styles.dateRefItem}>
                                    <Text style={styles.dateRefLabel}>Rif. N°:</Text>
                                    <Text style={[styles.dateRefValue, { minWidth: 80 }]}>{pratica?.rif_numero || ''}</Text>
                                </View>
                            </View>

                            <SectionTitle>
                                Informazioni sulla persona su cui si intende svolgere attività investigativa, ricerche e raccolta di informazioni
                            </SectionTitle>

                            {/* Persona fisica */}
                            <Field label="NOME / DENOMINAZIONE:" value={soggetto?.nome} />
                            <View style={styles.fieldRow}>
                                <Field label="Nato/a a:" value={soggetto?.nato_a} style={{ flex: 2 }} />
                                <Field label="in data:" value={formatDate(soggetto?.data_nascita)} style={{ flex: 1 }} />
                            </View>
                            <Field label="Residente in via:" value={soggetto?.indirizzo} />
                            <View style={styles.fieldRow}>
                                <Field label="C.A.P:" value={soggetto?.cap} style={{ flex: 0.8 }} />
                                <Field label="Comune:" value={soggetto?.citta} style={{ flex: 2 }} />
                                <Field label="Prov:" value={soggetto?.prov} style={{ flex: 0.6 }} />
                            </View>
                            <Field label="Codice Fiscale:" value={soggetto?.codice_fiscale} />

                            {/* Azienda */}
                            <SectionTitle>In caso di ditta individuale o azienda</SectionTitle>
                            <View style={styles.fieldRow}>
                                <Field label="Con sede in via:" value={soggetto?.sede_via} style={{ flex: 2 }} />
                                <Field label="C.A.P:" value={soggetto?.sede_cap} style={{ flex: 0.7 }} />
                            </View>
                            <View style={styles.fieldRow}>
                                <Field label="Comune:" value={soggetto?.sede_citta} style={{ flex: 2 }} />
                                <Field label="Prov:" value={soggetto?.sede_prov} style={{ flex: 0.8 }} />
                            </View>
                            <Field label="P. IVA / Codice Fiscale:" value={soggetto?.piva} />

                            {/* Recapiti */}
                            <SectionTitle>Recapiti e contatti</SectionTitle>
                            <Field label="Recapiti telefonici:" value={soggetto?.telefono} />
                            <Field label="Indirizzo e-mail:" value={soggetto?.email} />
                            <Field label="Nominativi su social network:" value={soggetto?.social} />

                            {/* Domicilio */}
                            <SectionTitle>Domicilio (se diverso da residenza) e altre pertinenze</SectionTitle>
                            <TextBox value={soggetto?.domicilio_altrove} lines={4} />

                            {/* Luoghi */}
                            <SectionTitle>Luoghi frequentati</SectionTitle>
                            <TextBox value={soggetto?.luoghi_frequentati} lines={4} />
                            <PageFooter pageNum={base + 1} />
                        </Page>

                        {/* Pagina 2: mezzi, lavoro, aspetto fisico */}
                        <Page size="A4" style={styles.page}>
                            <PageHeader logoUrl={logoUrl} dataCompilazione={pratica?.data_compilazione}
                                        rifero={pratica?.rif_numero} settings={settings}
                                        index={index} total={soggetti.length} pageNum={base + 2} totalPages={totalPages} />
                            <Text fixed style={styles.footerPageNum}
                                  render={({ pageNumber, totalPages }) => `Pag. ${pageNumber} di ${totalPages}`} />


                            <SectionTitle>
                                Veicoli di proprietà{'\n'}
                                <Text style={{ fontSize: 8, fontStyle: 'italic', textTransform: 'none', color: C.midGray }}>
                                    (indicare: intestazione, modello, colore, targa, uso esclusivo o condiviso, parcheggio abituale, orari)
                                </Text>
                            </SectionTitle>
                            <TextBox value={soggetto?.auto} lines={4} />

                            <SectionTitle>
                                Veicoli di famigliari{'\n'}
                                <Text style={{ fontSize: 8, fontStyle: 'italic', textTransform: 'none', color: C.midGray }}>
                                    (autovetture in uso da parte di familiari o conoscenti stretti del soggetto)
                                </Text>
                            </SectionTitle>
                            <TextBox value={soggetto?.auto_conoscenti} lines={4} />

                            <SectionTitle>
                                Motocicli{'\n'}
                                <Text style={{ fontSize: 8, fontStyle: 'italic', textTransform: 'none', color: C.midGray }}>
                                    (cilindrata, modello, colore, targa, abituale luogo di sosta)
                                </Text>
                            </SectionTitle>
                            <TextBox value={soggetto?.moto} lines={4} />

                            <SectionTitle>
                                Attività lavorativa{'\n'}
                                <Text style={{ fontSize: 8, fontStyle: 'italic', textTransform: 'none', color: C.midGray }}>
                                    (indicare: ruolo, orario, denominazione sociale, indirizzo)
                                </Text>
                            </SectionTitle>
                            <TextBox value={soggetto?.lavoro} lines={4} />

                            <SectionTitle>Hobby, sport, abitudini, frequentazione di luoghi</SectionTitle>
                            <TextBox value={`${soggetto?.hobby || ''}${soggetto?.hobby && soggetto?.abitudini ? '\n' : ''}${soggetto?.abitudini || ''}`.trim()} lines={4} />

                            <PageFooter pageNum={base + 2} />
                        </Page>

                        {/* Pagina 3: sicurezza, documenti, firma */}
                        <Page size="A4" style={styles.page}>
                            <PageHeader logoUrl={logoUrl} dataCompilazione={pratica?.data_compilazione}
                                        rifero={pratica?.rif_numero} settings={settings}
                                        index={index} total={soggetti.length} pageNum={base + 3} totalPages={totalPages} />
                            <Text fixed style={styles.footerPageNum}
                                  render={({ pageNumber, totalPages }) => `Pag. ${pageNumber} di ${totalPages}`} />

                            <SectionTitle>
                                Descrizione fisica{'\n'}
                                <Text style={{ fontSize: 8, fontStyle: 'italic', textTransform: 'none', color: C.midGray }}>
                                    (sesso, età approssimativa, peso, altezza, segni particolari)
                                </Text>
                            </SectionTitle>
                            <TextBox value={soggetto?.fisico} lines={4} />

                            {/* Booleani pag. 2 */}
                            <SectionTitle>Valutazioni di sicurezza</SectionTitle>
                            <BoolField label="Il soggetto è già stato osservato / pedinato in passato?" value={soggetto?.gia_pedinato} inBianco={!!data.inBianco} />
                            <BoolField label="Il soggetto è particolarmente attento o sospettoso?" value={soggetto?.attento} inBianco={!!data.inBianco} />
                            <BoolField label="Svolge attività lavorativa particolare (Forze dell'Ordine, Avvocato, Magistrato)?" value={soggetto?.lavoro_particolare} inBianco={!!data.inBianco} />
                            <BoolField label="Il soggetto è pericoloso?" value={soggetto?.pericoloso} inBianco={!!data.inBianco} />
                            <BoolField label="Possiede porto d'armi e/o armi?" value={soggetto?.armi} inBianco={!!data.inBianco} />

                            {/* Altre info casa */}
                            <SectionTitle>
                                Altre informazioni{'\n'}
                                <Text style={{ fontSize: 8, fontStyle: 'italic', textTransform: 'none', color: C.midGray }}>
                                    (tipo di abitazione, colore, disposizione finestre; caratteristiche della zona; animali domestici; composizione familiare; presenza di colf o collaboratori domestici; ecc.)
                                </Text>
                            </SectionTitle>
                            <TextBox value={soggetto?.altre_info_casa} lines={4} />

                            {/* Obblighi del mandante */}
                            <View style={styles.noteBox}>
                                <Text style={styles.noteText}>
                                    Il mandante è tenuto a dichiarare tutto ciò che conosce sul soggetto sottoposto ad attività investigativa. Lo stesso mandante ha l'obbligo di informare su eventuali variazioni relativamente alle informazioni qui sopra riportate e da esse stesso sottoscritte, eventuali variazioni di atteggiamento/i, di abitudini e di qualunque informazione possa essere utile ai fini dell'attività investigativa stessa e/o utile per l'immediata sospensione dell'attività investigativa stessa (per motivi di sicurezza e di opportunità del servizio stesso). Se il soggetto sottoposto ad investigativa è stato già osservato/pedinato, il mandante ha l'obbligo di informare l'Agenzia. Se il mandante ha conferito o conferirà incarico ad altro istituto investigativo per il controllo del medesimo soggetto, ha l'obbligo di informare l'Agenzia.
                                </Text>
                            </View>

                            {/* Documenti allegati */}
                            <SectionTitle>Documenti allegati</SectionTitle>
                            <TextBox value={soggetto?.allegati_nomi} lines={4} />

                            {/* Firma finale con ancora #otp_s{index+1}# invisibile (bianco su #fafafa, font minimo).
                                Ogni soggetto ha la sua ancora, così OTP mette una firma per ciascuno. */}
                            <View style={styles.signatureRow}>
                                <View style={{ flexDirection: 'row', alignItems: 'flex-end', flex: 1 }}>
                                    <Text style={styles.signatureLabel}>IL MANDANTE</Text>
                                    <View style={styles.signatureBox}>
                                        <Text style={styles.signatureArrow}></Text>
                                        <Text style={{ fontSize: 0.01, color: '#ffffff' }}>{`#otp_s${index + 1}#`}</Text>
                                    </View>
                                </View>
                            </View>
                            <PageFooter pageNum={base + 3} />
                        </Page>

                    </React.Fragment>
                );
            })}
        </Document>
    );
};

export default DatiSoggettoPDF;